import { useRef, useState } from "react";
import { formatShortDate, formatShortTime } from "src/utils/date";
import { StoreCustomer, StoreCustomerEdge } from "src/interfaces/customer.interface";
import useOutsideClick from "src/hooks/useOutsideClick";

const BORDER_LIGHT = "rgba(0,0,0,0.06)";

export type CustomerSortKey = "name" | "visits" | "lastVisit" | null;
export type CustomerSortOrder = "asc" | "desc";

const ROWS_PER_PAGE = 10;

export interface CustomersTableProps {
  edges: StoreCustomerEdge[];
  sortKey?: CustomerSortKey;
  sortOrder?: CustomerSortOrder;
  onSort?: (key: CustomerSortKey) => void;
  onViewCustomer?: (customer: StoreCustomer) => void;
  onEditCustomer?: (customer: StoreCustomer) => void;
  onDeleteCustomer?: (customer: StoreCustomer) => void;
}

function formatLocation(node: StoreCustomer): { city: string; state: string } {
  const city = (node.city ?? "").trim();
  const state = (node.state ?? "").trim();
  return { city: city || "-", state: state || "" };
}

function RowActionsMenu({
  onClose,
  customer,
  onView,
  onEdit,
  onDelete,
}: {
  onClose: () => void;
  customer: StoreCustomer;
  onView?: (customer: StoreCustomer) => void;
  onEdit?: (customer: StoreCustomer) => void;
  onDelete?: (customer: StoreCustomer) => void;
}) {
  return (
    <div
      className="absolute right-0 top-full mt-1 z-10 min-w-[128px] py-1 bg-white rounded-xl border border-[#F1F1F2] shadow-[0_4px_12px_rgba(0,0,0,0.08)] overflow-hidden"
      role="menu"
    >
      <button
        type="button"
        className="w-full text-left px-4 py-2.5 text-sm text-[#828282] hover:bg-[#f1f1f2] hover:text-[#252525]"
        onClick={() => {
          onView?.(customer);
          onClose();
        }}
      >
        View
      </button>
      <button
        type="button"
        className="w-full text-left px-4 py-2.5 text-sm text-[#828282] hover:bg-[#f1f1f2] hover:text-[#252525]"
        onClick={() => {
          onEdit?.(customer);
          onClose();
        }}
      >
        Edit
      </button>
      <button
        type="button"
        className="w-full text-left px-4 py-2.5 text-sm text-[#828282] hover:bg-red-50 hover:text-red-600"
        onClick={() => {
          onDelete?.(customer);
          onClose();
        }}
      >
        Delete
      </button>
    </div>
  );
}

function RowActions({
  customer,
  onView,
  onEdit,
  onDelete,
}: {
  customer: StoreCustomer;
  onView?: (customer: StoreCustomer) => void;
  onEdit?: (customer: StoreCustomer) => void;
  onDelete?: (customer: StoreCustomer) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOutsideClick({ ref, handler: () => setOpen(false) });

  return (
    <div
      ref={ref}
      className="rowActions relative flex justify-end opacity-0 transition-opacity duration-[120ms] ease-out group-hover:opacity-100 group-focus-within:opacity-100"
    >
      <button
        type="button"
        className="w-10 h-10 min-w-[40px] min-h-[40px] flex items-center justify-center rounded-[10px] text-gray-400 hover:text-gray-600 active:bg-gray-100"
        aria-label="Row actions"
        onClick={() => setOpen((o) => !o)}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
          <circle cx="10" cy="4" r="1.5" />
          <circle cx="10" cy="10" r="1.5" />
          <circle cx="10" cy="16" r="1.5" />
        </svg>
      </button>
      {open && (
        <RowActionsMenu
          customer={customer}
          onClose={() => setOpen(false)}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      )}
    </div>
  );
}

function SortIcon({
  sortKey,
  columnKey,
  sortOrder,
  onSort,
}: {
  sortKey: CustomerSortKey;
  columnKey: CustomerSortKey;
  sortOrder: CustomerSortOrder;
  onSort?: (key: CustomerSortKey) => void;
}) {
  const isActive = sortKey === columnKey;
  if (!onSort || columnKey === null) return null;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onSort(columnKey);
      }}
      className={`ml-1 inline-flex p-0.5 rounded transition-opacity ${
        isActive ? "opacity-100 text-orange" : "opacity-0 group-hover:opacity-60 hover:!opacity-100 text-gray-500"
      }`}
      aria-label={isActive ? `Sorted ${sortOrder}` : `Sort by ${columnKey}`}
    >
      {isActive && sortOrder === "desc" ? (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M5 8l5 5 5-5H5z" />
        </svg>
      ) : (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M15 12l-5-5-5 5h10z" />
        </svg>
      )}
    </button>
  );
}

/** Wide enough that last visit + location never share one cramped slice (avoids overlap on mobile scroll). */
const TABLE_MIN_WIDTH = 920;
const stickyHeaderClass = "sticky top-0 z-10 bg-[#f1f5f9]";

const CustomersTable = ({
  edges,
  sortKey = null,
  sortOrder = "asc",
  onSort,
  onViewCustomer,
  onEditCustomer,
  onDeleteCustomer,
}: CustomersTableProps) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const rows = (edges ?? []).slice(0, ROWS_PER_PAGE);

  return (
    <div className="w-full h-full flex flex-col min-h-0 overflow-hidden">
      <div className="flex-1 min-h-0 min-w-0 overflow-x-auto overflow-y-auto overscroll-x-contain">
        <table
          className="w-full text-left border-collapse table-auto"
          style={{ minWidth: TABLE_MIN_WIDTH }}
        >
          <thead>
            <tr className="text-[12px] font-semibold uppercase tracking-wide text-[#374151] border-b border-[#e2e8f0]">
              <th className={`py-3 pl-4 pr-3 align-middle whitespace-nowrap group cursor-pointer ${stickyHeaderClass}`}>
                <span className="inline-flex items-center">
                  Name
                  <SortIcon
                    sortKey={sortKey}
                    columnKey="name"
                    sortOrder={sortOrder}
                    onSort={onSort}
                  />
                </span>
              </th>
              <th className={`py-3 px-3 align-middle whitespace-nowrap ${stickyHeaderClass}`}>
                Email
              </th>
              <th className={`py-3 px-3 align-middle whitespace-nowrap ${stickyHeaderClass}`}>
                Phone
              </th>
              <th className={`py-3 px-3 align-middle whitespace-nowrap group cursor-pointer ${stickyHeaderClass}`}>
                <span className="inline-flex items-center">
                  Visits
                  <SortIcon
                    sortKey={sortKey}
                    columnKey="visits"
                    sortOrder={sortOrder}
                    onSort={onSort}
                  />
                </span>
              </th>
              <th
                className={`py-3 px-3 align-middle whitespace-nowrap group cursor-pointer min-w-[152px] ${stickyHeaderClass}`}
              >
                <span className="inline-flex items-center">
                  Last visit
                  <SortIcon
                    sortKey={sortKey}
                    columnKey="lastVisit"
                    sortOrder={sortOrder}
                    onSort={onSort}
                  />
                </span>
              </th>
              <th className={`py-3 px-3 align-middle whitespace-nowrap min-w-[128px] ${stickyHeaderClass}`}>
                Location
              </th>
              <th className={`py-3 pl-3 pr-4 align-middle ${stickyHeaderClass}`}>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ node }: StoreCustomerEdge, index: number) => {
            const loc = formatLocation(node);
            const isSelected = selectedId === (node.correlationId ?? "");
            return (
              <tr
                key={node.correlationId ?? index}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedId(node.correlationId ?? "")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSelectedId(node.correlationId ?? "");
                  }
                }}
                className={`customerRow group h-12 transition-[background-color] duration-[120ms] ease-out hover:bg-[#fff7f2] active:bg-[#ffe8dc] cursor-pointer ${
                  isSelected ? "bg-[#ffe8dc]" : ""
                }`}
                style={{ borderBottom: `1px solid ${BORDER_LIGHT}` }}
              >
                <td className="py-0 pl-4 pr-3 align-middle overflow-hidden">
                  <div className="py-1.5 min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate" title={node.name?.trim() || undefined}>
                      {node.name?.trim() || "-"}
                    </div>
                  </div>
                </td>
                <td className="py-0 px-3 align-middle overflow-hidden">
                  <div className="text-sm text-gray-600 truncate" title={node.email?.trim() || undefined}>
                    {node.email?.trim() || "-"}
                  </div>
                </td>
                <td className="text-sm text-gray-600 py-0 px-3 align-middle overflow-hidden">
                  <div className="truncate" title={node.phone ?? undefined}>{node.phone ?? "-"}</div>
                </td>
                <td className="py-0 px-3 align-middle">
                  {node.visitTimes != null ? (
                    <span className="inline-flex items-center rounded-full bg-[#FFF4EE] text-orange font-semibold text-xs px-2.5 py-1">
                      {node.visitTimes}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-500">-</span>
                  )}
                </td>
                <td className="min-w-[152px] max-w-[11rem] overflow-hidden px-3 py-0 align-top">
                  {node.lastVisitTime != null ? (
                    <div className="py-1.5 text-sm leading-tight text-[#111827]">
                      <div className="text-[#6B7280]">{formatShortTime(node.lastVisitTime)}</div>
                      <div className="font-medium">{formatShortDate(node.lastVisitTime)}</div>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500">-</span>
                  )}
                </td>
                <td className="min-w-[128px] max-w-[10rem] overflow-hidden px-3 py-0 align-top">
                  <div
                    className="py-1.5 text-sm truncate"
                    title={
                      loc.city !== "-" || loc.state
                        ? `${loc.city}${loc.state ? `, ${loc.state}` : ""}`
                        : undefined
                    }
                  >
                    {loc.city !== "-" || loc.state ? (
                      <>
                        <span className="font-medium text-gray-900">{loc.city}</span>
                        {loc.state && (
                          <span className="text-gray-500">, {loc.state}</span>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-500">-</span>
                    )}
                  </div>
                </td>
                <td className="py-0 pl-3 pr-4 align-middle" onClick={(e) => e.stopPropagation()}>
                  <RowActions
                    customer={node}
                    onView={onViewCustomer}
                    onEdit={onEditCustomer}
                    onDelete={onDeleteCustomer}
                  />
                </td>
              </tr>
            );
          })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CustomersTable;
